import { SubmitButton } from '@components/FormComponents';
import { TitleTypography } from '../styles';
import {
  OrderSummaryCard,
  OrderSummaryContainer,
  OrderSummaryDataContainer,
  OrderSummaryDetailContainer,
  SubtotalTypography,
  TotalTypography,
  DetailTypography,
  TotalContainer,
  HorizontalContainer,
  DiscountSpan,
} from './OrderSummary.styles';
import { Divider, Skeleton } from '@mui/material';
import { useOrderState } from '@hooks/useOrderState';
import { NEXT_PUBLIC_NO_PRICE_FALLBACK } from '@config/environment';
import { CurrencyFormatter } from '@utils/formatters/CurrencyFormatter';
import { useAuth } from '@hooks/useAuth';
import { usePermissions } from '@hooks/usePermissions';
import { useMemo } from 'react';
import { usePaymentSelectors } from '../paymentMethod/zustand/paymentMethodStore';

const currencyFormatter = new CurrencyFormatter({
  fallback: NEXT_PUBLIC_NO_PRICE_FALLBACK,
});

const zeroCurrencyFormatter = new CurrencyFormatter({
  fallback: '$0',
});

interface OrderSummaryProps {
  isDelivery: boolean;
  deliveryCost: number;
  oldDeliveryCost?: number;
  cardData?: string;
  handleNext?: () => void;
  buttonText?: string;
  variant?: 'desktop' | 'simple' | 'fixed-bottom';
  showDelivery?: boolean;
  showInsurance?: boolean;
  showMekiCredits?: boolean;
  loading?: boolean;
}

export const OrderSummary = ({
  isDelivery,
  deliveryCost,
  oldDeliveryCost,
  cardData,
  handleNext,
  buttonText = 'Siguiente',
  variant = 'desktop',
  showDelivery = true,
  showInsurance = true,
  showMekiCredits = true,
  loading: parentLoading,
}: OrderSummaryProps) => {
  const { userCan } = usePermissions();
  const isLocalVendor = userCan('LOCAL_VENDOR');
  const { appUser: user } = useAuth();

  const { order: currentOrder, loadingCurrentOrder } = useOrderState();
  const { installments } = currentOrder?.metadata || {};

  const {
    mekiCreditsChecked,
    insuranceId,
    totalReimbursement,
    totalDeductible,
    totalCopay,
  } = usePaymentSelectors();

  const toPayForReimbursedProducts = Math.max(
    0,
    (totalReimbursement ?? 0) - (totalDeductible ?? 0),
  );

  const sellDeductible = insuranceId
    ? totalCopay - toPayForReimbursedProducts
    : 0;

  const productsSubTotal = useMemo(() => {
    return currentOrder?.orderDetails.reduce(
      (acc, curr) =>
        acc +
        (curr.product.discount?.price ?? curr.product.price) * curr.quantity,
      0,
    );
  }, [currentOrder?.orderDetails]);

  const productsQuantity = useMemo(() => {
    return currentOrder?.orderDetails.reduce(
      (acc, curr) => acc + curr.quantity,
      0,
    );
  }, [currentOrder?.orderDetails]);

  const userMekiCredits = user?.credits ?? 0;
  const { usedMekiCredits, totalFinal } = useMemo(() => {
    const subTotal = productsSubTotal + (showDelivery ? deliveryCost : 0);
    const usedMekiCredits =
      showMekiCredits && mekiCreditsChecked
        ? Math.min(subTotal, userMekiCredits)
        : 0;
    const insuranceTotalReimbursement =
      showInsurance && Boolean(insuranceId) ? toPayForReimbursedProducts : 0;
    return {
      usedMekiCredits,
      totalFinal: subTotal - insuranceTotalReimbursement - usedMekiCredits,
    };
  }, [
    productsSubTotal,
    deliveryCost,
    totalReimbursement,
    totalDeductible,
    userMekiCredits,
    mekiCreditsChecked,
    insuranceId,
    showInsurance,
    showMekiCredits,
    showDelivery,
  ]);

  const loading = loadingCurrentOrder || parentLoading;
  return (
    <OrderSummaryCard variant={variant}>
      <OrderSummaryContainer>
        {variant === 'desktop' && (
          <TitleTypography>Resumen compra</TitleTypography>
        )}
        {loading ? (
          <OrderSummarySkeleton variant={variant} />
        ) : (
          <OrderSummaryDataContainer variant={variant}>
            <OrderSummaryDetailContainer>
              <HorizontalContainer>
                <SubtotalTypography>
                  Productos ({productsQuantity})
                </SubtotalTypography>
                <SubtotalTypography>
                  {currencyFormatter.format(productsSubTotal)}
                </SubtotalTypography>
              </HorizontalContainer>
              {showInsurance &&
                Boolean(insuranceId) &&
                (totalReimbursement > 0 ||
                  totalDeductible > 0 ||
                  totalCopay > 0) && (
                  <>
                    <HorizontalContainer>
                      <DetailTypography>Reembolso póliza</DetailTypography>
                      <DetailTypography>
                        {zeroCurrencyFormatter.format(
                          totalReimbursement + totalDeductible,
                        )}
                      </DetailTypography>
                    </HorizontalContainer>
                    <HorizontalContainer>
                      <DetailTypography>Deducible póliza</DetailTypography>
                      <DetailTypography>
                        {zeroCurrencyFormatter.format(totalDeductible)}
                      </DetailTypography>
                    </HorizontalContainer>
                    <HorizontalContainer>
                      <DetailTypography bold>Aporte seguro</DetailTypography>
                      <DetailTypography bold>
                        {zeroCurrencyFormatter.format(totalReimbursement)}
                      </DetailTypography>
                    </HorizontalContainer>
                    <HorizontalContainer>
                      <DetailTypography>Deducible venta</DetailTypography>
                      <DetailTypography>
                        {zeroCurrencyFormatter.format(sellDeductible)}
                      </DetailTypography>
                    </HorizontalContainer>
                    <HorizontalContainer>
                      <DetailTypography bold>
                        Valor a pagar productos
                      </DetailTypography>
                      <DetailTypography bold>
                        {zeroCurrencyFormatter.format(
                          productsSubTotal - totalReimbursement,
                        )}
                      </DetailTypography>
                    </HorizontalContainer>
                  </>
                )}
              {showDelivery && (
                <>
                  {isDelivery ? (
                    <HorizontalContainer>
                      <DetailTypography>Despacho</DetailTypography>
                      <DetailTypography>
                        <DiscountSpan>
                          {oldDeliveryCost && oldDeliveryCost > deliveryCost
                            ? currencyFormatter.format(oldDeliveryCost)
                            : null}
                        </DiscountSpan>
                        {deliveryCost > 0
                          ? currencyFormatter.format(deliveryCost)
                          : 'Gratis'}
                      </DetailTypography>
                    </HorizontalContainer>
                  ) : null}
                </>
              )}
              {!isLocalVendor && mekiCreditsChecked && showMekiCredits && (
                <HorizontalContainer>
                  <DetailTypography>Créditos Meki</DetailTypography>
                  <DetailTypography>
                    -{zeroCurrencyFormatter.format(usedMekiCredits)}
                  </DetailTypography>
                </HorizontalContainer>
              )}
            </OrderSummaryDetailContainer>
            {variant !== 'fixed-bottom' && (
              <TotalContainer>
                <HorizontalContainer>
                  <TotalTypography>Total a pagar</TotalTypography>
                  <TotalTypography>
                    {zeroCurrencyFormatter.format(totalFinal)}
                  </TotalTypography>
                </HorizontalContainer>
                {installments > 0 && (
                  <HorizontalContainer>
                    <DetailTypography bold>Nº de cuotas</DetailTypography>
                    <DetailTypography bold>
                      {installments} cuotas
                    </DetailTypography>
                  </HorizontalContainer>
                )}
                {cardData && (
                  <HorizontalContainer>
                    <DetailTypography></DetailTypography>
                    <DetailTypography>{cardData}</DetailTypography>
                  </HorizontalContainer>
                )}
              </TotalContainer>
            )}
          </OrderSummaryDataContainer>
        )}
      </OrderSummaryContainer>
      {handleNext && (
        <SubmitButton disabled={loading} onClick={handleNext}>
          {buttonText}
        </SubmitButton>
      )}
    </OrderSummaryCard>
  );
};

const OrderSummarySkeleton = ({
  variant,
}: {
  variant: 'desktop' | 'simple' | 'fixed-bottom';
}) => (
  <OrderSummaryDataContainer variant={variant}>
    <OrderSummaryDetailContainer>
      <HorizontalContainer>
        <Skeleton variant="rounded" width={160} height={20} />
        <Skeleton variant="rounded" width={80} height={20} />
      </HorizontalContainer>
      <HorizontalContainer>
        <Skeleton variant="rounded" width={160} height={18} />
        <Skeleton variant="rounded" width={80} height={20} />
      </HorizontalContainer>
    </OrderSummaryDetailContainer>
    {variant !== 'fixed-bottom' && (
      <TotalContainer>
        <HorizontalContainer>
          <Skeleton variant="rounded" width={160} height={24} />
          <Skeleton variant="rounded" width={80} height={24} />
        </HorizontalContainer>
      </TotalContainer>
    )}
  </OrderSummaryDataContainer>
);
